/**
 * Asset Hooks
 * ===========
 * React Query hooks for IT asset operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { assetApi } from '@/services/api'
import type { AssetCreate, AssetSearchParams, AssetStatusUpdate, AssetUpdate } from '@/types'

// Query keys
export const assetKeys = {
  all: ['assets'] as const,
  lists: () => [...assetKeys.all, 'list'] as const,
  list: (params?: AssetSearchParams) => [...assetKeys.lists(), params] as const,
  details: () => [...assetKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...assetKeys.details(), id] as const,
  stats: () => [...assetKeys.all, 'stats'] as const,
}

/**
 * Hook to fetch paginated asset list.
 */
export function useAssets(params?: AssetSearchParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: assetKeys.list(params),
    queryFn: () => assetApi.list(params),
    enabled: options?.enabled ?? true,
  })
}

/**
 * Hook to fetch a single asset.
 */
export function useAsset(id: string | number) {
  return useQuery({
    queryKey: assetKeys.detail(id),
    queryFn: () => assetApi.get(id),
    enabled: !!id,
  })
}

/**
 * Hook to fetch asset statistics for dashboard.
 */
export function useAssetStats() {
  return useQuery({
    queryKey: assetKeys.stats(),
    queryFn: () => assetApi.getStats(),
  })
}

/**
 * Hook to create a new asset.
 */
export function useCreateAsset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: AssetCreate) => assetApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() })
      queryClient.invalidateQueries({ queryKey: assetKeys.stats() })
    },
  })
}

/**
 * Hook to update an asset.
 */
export function useUpdateAsset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: AssetUpdate }) =>
      assetApi.update(id, data),
    onSuccess: (response, { id }) => {
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() })
      queryClient.invalidateQueries({ queryKey: assetKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: assetKeys.stats() })
      // Also invalidate by asset_id if available (for when viewing by AST-XXXX)
      if (response?.data?.asset_id) {
        queryClient.invalidateQueries({ queryKey: assetKeys.detail(response.data.asset_id) })
      }
    },
  })
}

/**
 * Hook to delete an asset.
 */
export function useDeleteAsset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string | number) => assetApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() })
      queryClient.invalidateQueries({ queryKey: assetKeys.stats() })
    },
  })
}

/**
 * Hook to refresh asset status via MCP integration.
 */
export function useRefreshAssetStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string | number) => assetApi.refreshStatus(id),
    onSuccess: (response, id) => {
      queryClient.invalidateQueries({ queryKey: assetKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() })
      queryClient.invalidateQueries({ queryKey: assetKeys.stats() })
      // Also invalidate by asset_id if available
      if (response?.data?.asset_id) {
        queryClient.invalidateQueries({ queryKey: assetKeys.detail(response.data.asset_id) })
      }
    },
  })
}

/**
 * Hook to manually update asset status.
 */
export function useUpdateAssetStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: AssetStatusUpdate }) =>
      assetApi.updateStatus(id, data),
    onSuccess: (response, { id }) => {
      queryClient.invalidateQueries({ queryKey: assetKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() })
      queryClient.invalidateQueries({ queryKey: assetKeys.stats() })
      // Also invalidate by asset_id if available
      if (response?.data?.asset_id) {
        queryClient.invalidateQueries({ queryKey: assetKeys.detail(response.data.asset_id) })
      }
    },
  })
}
